import { Button, Descriptions, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type CtsModuleSettingDataFragment } from '../../../../../api/fragments/CtsModuleSettingData';
import { type CtsModuleSpecsFragment } from '../../../../../api/fragments/CtsModuleSpecs';
import ConsolePageWithHeader from '../../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { HeaderTabs } from '../types';
import CtsSettingTabFinanceProduct from './CtsSettingTabFinanceProduct';
import CtsSettingTabInsuranceProduct from './CtsSettingTabInsuranceProduct';
import CtsSettingTabMainDetails from './CtsSettingTabMainDetails';

type UpdateCtsInnerTabProps = {
    activeTab: HeaderTabs;
    module: CtsModuleSpecsFragment;
    setting: CtsModuleSettingDataFragment;
};

const UpdateCtsInnerTab = ({ activeTab, module, setting }: UpdateCtsInnerTabProps) => {
    switch (activeTab) {
        case HeaderTabs.MainDetails:
            return <CtsSettingTabMainDetails module={module} setting={setting} />;

        case HeaderTabs.FinanceProducts:
            return <CtsSettingTabFinanceProduct module={module} setting={setting} />;

        case HeaderTabs.InsuranceProducts:
            return <CtsSettingTabInsuranceProduct module={module} setting={setting} />;

        default:
            return null;
    }
};

export type UpdateCtsSettingInnerPageProps = {
    module: CtsModuleSpecsFragment;
    setting: CtsModuleSettingDataFragment;
};

const UpdateCtsSettingInnerPage = ({ module, setting }: UpdateCtsSettingInnerPageProps) => {
    const { t } = useTranslation(['ctsModuleDetails']);

    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('ctsModuleDetails:descriptions.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('ctsModuleDetails:descriptions.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('ctsModuleDetails:descriptions.financingModule')}>
                {setting.bankModule.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('ctsModuleDetails:descriptions.vehicleModule')}>
                {setting.vehicleModules.map(vehicleModule => vehicleModule.displayName).join(', ')}
            </Descriptions.Item>
            {setting.insuranceModule && (
                <Descriptions.Item label={t('ctsModuleDetails:descriptions.insuranceModule')}>
                    {setting.insuranceModule.displayName}
                </Descriptions.Item>
            )}
        </Descriptions>
    );

    const availableTabs = useMemo(
        () => [
            {
                label: t('ctsModuleDetails:tabs.mainDetails.title'),
                key: HeaderTabs.MainDetails,
                show: true,
            },
            {
                label: t('ctsModuleDetails:tabs.financeProducts.title'),
                key: HeaderTabs.FinanceProducts,
                show: true,
            },
            {
                label: t('ctsModuleDetails:tabs.insuranceProducts.title'),
                key: HeaderTabs.InsuranceProducts,
                show: setting.insuranceModule !== null,
            },
        ],
        [setting.insuranceModule, t]
    );

    return (
        <ConsolePageWithHeader
            content={content}
            footer={
                activeTab === HeaderTabs.MainDetails && [
                    <Button form="updateMainDetails" htmlType="submit" type="primary">
                        {t('ctsModuleDetails:actions.update')}
                    </Button>,
                ]
            }
            header={{
                footer: (
                    <Tabs
                        activeKey={activeTab}
                        items={availableTabs.filter(tabs => tabs.show)}
                        onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                    />
                ),
            }}
            onBack={() => navigate(`/admin/system/modules/${module.id}`)}
            title={t('ctsModuleDetails:settings.title')}
        >
            <UpdateCtsInnerTab activeTab={activeTab} module={module} setting={setting} />
        </ConsolePageWithHeader>
    );
};

export default UpdateCtsSettingInnerPage;
